import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  ArrowUpTrayIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';
import {
  badgeClass,
  Field,
  getApiErrorMessage,
  inputClass,
  inputErrorClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../../components/ui/FormControls';
import companySettingsService, { CompanySettings, CompanySettingsInput } from '../../services/company-settings.service';
import { INDIAN_STATES } from '../../types/customer.types';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../lib/utils';

// The same formats the server checks
const GSTIN_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const PAN_PATTERN = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const TAN_PATTERN = /^[A-Z]{4}[0-9]{5}[A-Z]$/;
const CIN_PATTERN = /^[LU][0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/;
const IFSC_PATTERN = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const SWIFT_PATTERN = /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_PATTERN = /^[0-9+()\-\s]{6,20}$/;
const WEBSITE_PATTERN = /^https?:\/\/[^\s.]+\.[^\s]+$/i;
// Keeps a save under the server's request size limit
const MAX_LOGO_LENGTH = 80000;

const CURRENCIES = [
  { value: 'INR', label: 'INR – Indian Rupee' },
  { value: 'USD', label: 'USD – US Dollar' },
  { value: 'EUR', label: 'EUR – Euro' },
  { value: 'GBP', label: 'GBP – Pound Sterling' },
  { value: 'AED', label: 'AED – UAE Dirham' },
  { value: 'SGD', label: 'SGD – Singapore Dollar' },
  { value: 'JPY', label: 'JPY – Japanese Yen' },
  { value: 'AUD', label: 'AUD – Australian Dollar' },
  { value: 'CAD', label: 'CAD – Canadian Dollar' },
];
const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'Hindi' },
  { value: 'mr', label: 'Marathi' },
  { value: 'gu', label: 'Gujarati' },
  { value: 'ta', label: 'Tamil' },
  { value: 'te', label: 'Telugu' },
  { value: 'kn', label: 'Kannada' },
  { value: 'bn', label: 'Bengali' },
];
const TIMEZONES = ['Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore', 'Asia/Tokyo', 'Europe/London', 'Europe/Berlin', 'America/New_York', 'America/Los_Angeles', 'Australia/Sydney', 'UTC'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// Every value is kept as text while editing
type FormState = {
  companyName: string;
  legalName: string;
  companyCode: string;
  description: string;
  logo: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  country: string;
  phone: string;
  mobile: string;
  email: string;
  website: string;
  gstNumber: string;
  panNumber: string;
  tanNumber: string;
  cinNumber: string;
  bankName: string;
  accountHolderName: string;
  accountNumber: string;
  ifscCode: string;
  bankBranch: string;
  swiftCode: string;
  currency: string;
  language: string;
  timezone: string;
  financialYearStartMonth: string;
  paymentTermDays: string;
  creditLimitAction: string;
  purchaseApprovalLimit: string;
  invoiceTerms: string;
  invoiceNotes: string;
};

const EMPTY_FORM: FormState = {
  companyName: '',
  legalName: '',
  companyCode: '',
  description: '',
  logo: '',
  address: '',
  city: '',
  state: '',
  pincode: '',
  country: 'India',
  phone: '',
  mobile: '',
  email: '',
  website: '',
  gstNumber: '',
  panNumber: '',
  tanNumber: '',
  cinNumber: '',
  bankName: '',
  accountHolderName: '',
  accountNumber: '',
  ifscCode: '',
  bankBranch: '',
  swiftCode: '',
  currency: 'INR',
  language: 'en',
  timezone: 'Asia/Kolkata',
  financialYearStartMonth: '4',
  paymentTermDays: '30',
  creditLimitAction: 'warn',
  purchaseApprovalLimit: '0',
  invoiceTerms: '',
  invoiceNotes: '',
};

const toForm = (settings: CompanySettings | null): FormState => {
  if (!settings) return EMPTY_FORM;
  const form = { ...EMPTY_FORM };
  (Object.keys(EMPTY_FORM) as (keyof FormState)[]).forEach((key) => {
    const value = (settings as any)[key];
    if (value !== null && value !== undefined) form[key] = String(value);
  });
  return form;
};

// A company of the master data as a starting point for the settings
const fromCompany = (company: Record<string, any>): FormState => ({
  ...EMPTY_FORM,
  companyName: company.name || '',
  legalName: company.legalName || '',
  companyCode: company.code || '',
  description: company.description || '',
  address: company.address || '',
  city: company.city || '',
  state: company.state || '',
  pincode: company.pincode || '',
  country: company.country || 'India',
  phone: company.phone || '',
  mobile: company.mobile || '',
  email: company.email || '',
  website: company.website || '',
  gstNumber: company.gstNumber || '',
  panNumber: company.panNumber || '',
  tanNumber: company.tanNumber || '',
  cinNumber: company.cinNumber || '',
  bankName: company.bankName || '',
  accountNumber: company.accountNumber || '',
  ifscCode: company.ifscCode || '',
  swiftCode: company.swiftCode || '',
  currency: company.baseCurrency || 'INR',
  language: company.language || 'en',
  timezone: company.timezone || 'Asia/Kolkata',
});

const IDENTIFIER_FIELDS: (keyof FormState)[] = ['gstNumber', 'panNumber', 'tanNumber', 'cinNumber', 'ifscCode', 'swiftCode', 'accountNumber'];

const validate = (form: FormState): Partial<Record<keyof FormState, string>> => {
  const errors: Partial<Record<keyof FormState, string>> = {};
  const text = (key: keyof FormState) => form[key].trim();

  if (text('companyName').length < 2) errors.companyName = 'Enter the company name';
  if (text('address').length < 5) errors.address = 'Enter the address';
  if (text('pincode') && !/^[0-9]{6}$/.test(text('pincode'))) errors.pincode = 'The pincode must be 6 digits';
  if (text('phone') && !PHONE_PATTERN.test(text('phone'))) errors.phone = 'Enter a valid phone number';
  if (text('mobile') && !PHONE_PATTERN.test(text('mobile'))) errors.mobile = 'Enter a valid mobile number';
  if (text('email') && !EMAIL_PATTERN.test(text('email'))) errors.email = 'Enter a valid email address';
  if (text('website') && !WEBSITE_PATTERN.test(text('website'))) errors.website = 'Start with http:// or https://';

  const gstin = text('gstNumber');
  if (gstin && !GSTIN_PATTERN.test(gstin)) errors.gstNumber = 'Enter a valid 15-character GST number';
  else if (gstin) {
    const state = INDIAN_STATES.find((entry) => entry.gstCode === gstin.slice(0, 2));
    if (!state) errors.gstNumber = `${gstin.slice(0, 2)} is not a state code`;
    else if (text('state') && INDIAN_STATES.some((entry) => entry.name === text('state')) && state.name !== text('state')) {
      errors.gstNumber = `This GST number belongs to ${state.name}, but the state is ${text('state')}`;
    }
  }
  const pan = text('panNumber');
  if (pan && !PAN_PATTERN.test(pan)) errors.panNumber = 'Enter a valid 10-character PAN';
  else if (pan && gstin && GSTIN_PATTERN.test(gstin) && gstin.slice(2, 12) !== pan) {
    errors.panNumber = `The GST number contains the PAN ${gstin.slice(2, 12)}`;
  }
  if (text('tanNumber') && !TAN_PATTERN.test(text('tanNumber'))) errors.tanNumber = 'Enter a valid 10-character TAN';
  if (text('cinNumber') && !CIN_PATTERN.test(text('cinNumber'))) errors.cinNumber = 'Enter a valid 21-character CIN';
  if (text('accountNumber') && !/^[0-9A-Z]{6,34}$/.test(text('accountNumber'))) errors.accountNumber = 'Use 6 to 34 letters or digits';
  if (text('ifscCode') && !IFSC_PATTERN.test(text('ifscCode'))) errors.ifscCode = 'Enter a valid 11-character IFSC code';
  if (text('swiftCode') && !SWIFT_PATTERN.test(text('swiftCode'))) errors.swiftCode = 'Enter a valid 8 or 11-character SWIFT code';

  const days = Number(form.paymentTermDays);
  if (form.paymentTermDays.trim() === '' || !Number.isInteger(days) || days < 0 || days > 365) {
    errors.paymentTermDays = 'Enter whole days from 0 to 365';
  }
  const approvalLimit = Number(form.purchaseApprovalLimit || 0);
  if (!Number.isFinite(approvalLimit) || approvalLimit < 0) errors.purchaseApprovalLimit = 'Enter an amount, or 0 for no limit';
  return errors;
};

// What goes to the server: an empty text clears an optional field
const toInput = (form: FormState): CompanySettingsInput => {
  const input: Record<string, unknown> = {};
  (Object.keys(form) as (keyof FormState)[]).forEach((key) => {
    input[key] = form[key].trim();
  });
  input.financialYearStartMonth = Number(form.financialYearStartMonth);
  input.paymentTermDays = Number(form.paymentTermDays);
  input.purchaseApprovalLimit = Number(form.purchaseApprovalLimit) || 0;
  return input as CompanySettingsInput;
};

// Scales a picked image down to letterhead size and returns it as a data URL
const readLogo = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      reject(new Error('Choose a PNG, JPEG or WebP image'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      reject(new Error('The image is larger than 5 MB'));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('The image could not be read'));
    reader.onload = () => {
      const image = new Image();
      image.onerror = () => reject(new Error('The image could not be read'));
      image.onload = () => {
        const scale = Math.min(1, 360 / image.width, 180 / image.height);
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(image.width * scale));
        canvas.height = Math.max(1, Math.round(image.height * scale));
        const context = canvas.getContext('2d');
        if (!context) {
          reject(new Error('The image could not be processed'));
          return;
        }
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        // PNG keeps a transparent background; JPEG is the fallback for photos that come out too large
        let result = canvas.toDataURL('image/png');
        if (result.length > MAX_LOGO_LENGTH) {
          context.globalCompositeOperation = 'destination-over';
          context.fillStyle = '#ffffff';
          context.fillRect(0, 0, canvas.width, canvas.height);
          result = canvas.toDataURL('image/jpeg', 0.85);
        }
        if (result.length > MAX_LOGO_LENGTH) reject(new Error('The image is too detailed. Use a simpler logo.'));
        else resolve(result);
      };
      image.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  });

const SECTIONS = [
  { id: 'identity', title: 'Company identity', description: 'The name and logo printed on documents.' },
  { id: 'address', title: 'Registered address', description: 'Where the company is registered; printed under the name.' },
  { id: 'contact', title: 'Contact', description: 'How customers and vendors reach you.' },
  { id: 'tax', title: 'Tax registrations', description: 'GST decides whether an invoice is charged CGST + SGST or IGST.' },
  { id: 'bank', title: 'Bank account', description: 'Printed on invoices so customers know where to pay.' },
  { id: 'regional', title: 'Regional settings', description: 'Currency, language, time zone and the financial year.' },
  { id: 'invoices', title: 'Invoice defaults', description: 'What a new invoice starts with. Each invoice can still be changed.' },
] as const;

type SectionId = (typeof SECTIONS)[number]['id'];

// What a complete profile has, by section
const CHECKLIST: { section: SectionId; label: string; done: (form: FormState) => boolean }[] = [
  { section: 'identity', label: 'Company name', done: (form) => form.companyName.trim().length >= 2 },
  { section: 'identity', label: 'Logo', done: (form) => !!form.logo },
  { section: 'address', label: 'Address', done: (form) => form.address.trim().length >= 5 },
  { section: 'address', label: 'City, state and pincode', done: (form) => !!form.city.trim() && !!form.state.trim() && !!form.pincode.trim() },
  { section: 'contact', label: 'Phone or email', done: (form) => !!form.phone.trim() || !!form.mobile.trim() || !!form.email.trim() },
  { section: 'tax', label: 'GST number', done: (form) => GSTIN_PATTERN.test(form.gstNumber.trim()) },
  { section: 'tax', label: 'PAN', done: (form) => PAN_PATTERN.test(form.panNumber.trim()) },
  { section: 'bank', label: 'Bank name, account number and IFSC', done: (form) => !!form.bankName.trim() && !!form.accountNumber.trim() && !!form.ifscCode.trim() },
  { section: 'invoices', label: 'Invoice terms', done: (form) => !!form.invoiceTerms.trim() },
];

const cardClass = 'rounded-lg bg-white shadow-sm ring-1 ring-gray-200';

const formatDateTime = (value: string) =>
  new Date(value).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const CompanySettingsScreen: React.FC = () => {
  const queryClient = useQueryClient();
  // Saving needs the "Company settings: Edit" permission
  const { can } = usePermissions();
  const canEdit = can('settings.update');

  const { data: overview, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['company-settings', 'overview'],
    queryFn: () => companySettingsService.getOverview(),
  });

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  // What is saved on the server, to tell whether anything was changed
  const [baseline, setBaseline] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [logoBusy, setLogoBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const loaded = useRef(false);

  // Fill the form once, when the settings arrive; later refetches never overwrite what is being typed
  useEffect(() => {
    if (!overview || loaded.current) return;
    loaded.current = true;
    const initial = toForm(overview.settings);
    setForm(initial);
    setBaseline(initial);
  }, [overview]);

  const isSetUp = !!overview?.settings;
  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(baseline), [form, baseline]);

  // Leaving the page with unsaved changes asks first
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const save = useMutation({
    mutationFn: (input: CompanySettingsInput) => companySettingsService.updateSettings(input),
    onSuccess: (saved) => {
      const next = toForm(saved);
      setForm(next);
      setBaseline(next);
      setErrors({});
      toast.success(isSetUp ? 'Company settings saved' : 'Company set up. New invoices now use these details.');
      queryClient.invalidateQueries({ queryKey: ['company-settings'] });
      // The seller offered to a new invoice comes from here
      queryClient.invalidateQueries({ queryKey: ['invoices', 'seller'] });
    },
    onError: (saveError) => toast.error(getApiErrorMessage(saveError, 'The company settings could not be saved')),
  });

  const setField = (name: keyof FormState, value: string) => {
    const next = IDENTIFIER_FIELDS.includes(name) ? value.toUpperCase().replace(/\s/g, '') : value;
    setForm((prev) => ({ ...prev, [name]: next }));
    if (errors[name]) {
      setErrors((prev) => {
        const { [name]: _removed, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canEdit) return;
    const found = validate(form);
    setErrors(found);
    const first = Object.keys(found)[0];
    if (first) {
      const element = document.getElementById(`cs-${first}`);
      element?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      element?.focus({ preventScroll: true });
      toast.error(`${Object.keys(found).length === 1 ? 'One field needs' : `${Object.keys(found).length} fields need`} attention`);
      return;
    }
    save.mutate(toInput(form));
  };

  const discard = () => {
    setForm(baseline);
    setErrors({});
  };

  const pickLogo = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setLogoBusy(true);
    try {
      setField('logo', await readLogo(file));
    } catch (logoError) {
      toast.error(logoError instanceof Error ? logoError.message : 'The image could not be used');
    } finally {
      setLogoBusy(false);
    }
  };

  // What the GST number itself says about the state and the PAN
  const gstin = form.gstNumber.trim();
  const gstState = GSTIN_PATTERN.test(gstin) ? INDIAN_STATES.find((entry) => entry.gstCode === gstin.slice(0, 2)) : undefined;
  const gstPan = GSTIN_PATTERN.test(gstin) ? gstin.slice(2, 12) : '';
  const canFillFromGst = !!gstState && (form.state !== gstState.name || form.panNumber !== gstPan);
  const fillFromGst = () => {
    if (!gstState) return;
    setForm((prev) => ({ ...prev, state: gstState.name, panNumber: gstPan, country: prev.country || 'India' }));
    setErrors((prev) => {
      const { gstNumber: _g, panNumber: _p, state: _s, ...rest } = prev;
      return rest;
    });
  };

  const fieldClass = (name: keyof FormState) => cn(inputClass, errors[name] && inputErrorClass);
  const isIndia = form.country.trim().toLowerCase() === 'india' || form.country.trim() === '';
  const stateIsListed = !form.state || INDIAN_STATES.some((entry) => entry.name === form.state);

  const completed = CHECKLIST.filter((item) => item.done(form)).length;
  const percent = Math.round((completed / CHECKLIST.length) * 100);
  const sectionState = (id: SectionId) => {
    const items = CHECKLIST.filter((item) => item.section === id);
    return { total: items.length, done: items.filter((item) => item.done(form)).length };
  };
  const errorCount = Object.keys(errors).length;

  const text = (name: keyof FormState, label: string, options: { required?: boolean; hint?: string; placeholder?: string; maxLength?: number; type?: string; className?: string; mono?: boolean } = {}) => (
    <Field label={label} htmlFor={`cs-${name}`} required={options.required} error={errors[name]} hint={options.hint} className={options.className}>
      <input
        id={`cs-${name}`}
        type={options.type ?? 'text'}
        value={form[name]}
        maxLength={options.maxLength}
        placeholder={options.placeholder}
        onChange={(event) => setField(name, event.target.value)}
        className={cn(fieldClass(name), options.mono && 'font-mono tracking-wide')}
      />
    </Field>
  );

  const section = (id: SectionId, children: React.ReactNode) => {
    const meta = SECTIONS.find((entry) => entry.id === id)!;
    const index = SECTIONS.findIndex((entry) => entry.id === id);
    return (
      <section id={`section-${id}`} className={cn(cardClass, 'scroll-mt-24')}>
        <div className="flex items-start gap-3 border-b border-gray-200 px-4 py-3">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-xs font-semibold text-white">
            {index + 1}
          </span>
          <div>
            <h2 className="text-sm font-semibold text-gray-900">{meta.title}</h2>
            <p className="mt-0.5 text-xs text-gray-500">{meta.description}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-x-4 gap-y-4 p-4 sm:grid-cols-2">{children}</div>
      </section>
    );
  };

  if (isError) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-semibold text-gray-900">Company Settings</h1>
        <div className={cn(cardClass, 'p-10 text-center')}>
          <p className="text-sm text-gray-700">{getApiErrorMessage(error, 'The company settings could not be loaded.')}</p>
          <button type="button" onClick={() => refetch()} className={cn(secondaryButtonClass, 'mt-4')}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  const previewAddress = [form.address.trim(), form.city.trim(), [form.state.trim(), form.pincode.trim()].filter(Boolean).join(' '), isIndia ? '' : form.country.trim()]
    .filter(Boolean)
    .join(', ');

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-6 pb-20">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Company Settings</h1>
          <p className="mt-1 text-sm text-gray-500">
            The profile of your business. It is the seller on invoices and the letterhead of printed documents.
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          {isLoading ? null : isSetUp ? (
            <>
              <span className={cn(badgeClass, 'bg-green-50 text-green-700 ring-green-600/20')}>Set up</span>
              <span>Last saved {formatDateTime(overview!.settings!.updatedAt)}</span>
            </>
          ) : (
            <span className={cn(badgeClass, 'bg-amber-50 text-amber-700 ring-amber-600/20')}>Not set up yet</span>
          )}
        </div>
      </div>

      {/* Where invoices take the seller from right now */}
      {overview && !isSetUp && (
        <div className="flex items-start gap-3 rounded-lg bg-amber-50 p-4 ring-1 ring-inset ring-amber-600/20">
          <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div className="min-w-0 flex-1 text-sm text-amber-900">
            <p className="font-medium">Your company is not set up yet.</p>
            <p className="mt-0.5">
              {overview.sellerSource === 'company'
                ? 'Until it is, new invoices take the seller from the primary company under Master Data.'
                : overview.sellerSource === 'last_invoice'
                  ? 'Until it is, each new invoice copies the seller details from the latest invoice.'
                  : 'Until it is, the seller details have to be typed on every invoice.'}{' '}
              Fill in at least the name and address and save.
            </p>
            {overview.primaryCompany && canEdit && (
              <button
                type="button"
                onClick={() => setForm(fromCompany(overview.primaryCompany as Record<string, any>))}
                className="mt-2 font-medium text-amber-900 underline hover:text-amber-700"
              >
                Start from {overview.primaryCompany.name}, the primary company
              </button>
            )}
          </div>
        </div>
      )}
      {overview && !canEdit && (
        <div className="flex items-start gap-3 rounded-lg bg-gray-50 p-4 ring-1 ring-inset ring-gray-200">
          <InformationCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-gray-400" />
          <p className="text-sm text-gray-700">You can see the company settings. Your role does not allow changing them.</p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_22rem]">
        {/* The settings */}
        <fieldset disabled={!canEdit || isLoading || save.isPending} className="min-w-0 space-y-6">
          {section(
            'identity',
            <>
              <div className="sm:col-span-2">
                <p className="block text-sm font-medium text-gray-700">Logo</p>
                <div className="mt-1 flex items-center gap-4">
                  <div className="flex h-20 w-36 shrink-0 items-center justify-center overflow-hidden rounded-md border border-dashed border-gray-300 bg-gray-50">
                    {form.logo ? (
                      <img src={form.logo} alt="Company logo" className="max-h-full max-w-full object-contain" />
                    ) : (
                      <PhotoIcon className="h-8 w-8 text-gray-300" />
                    )}
                  </div>
                  <div>
                    <div className="flex flex-wrap gap-2">
                      <button type="button" onClick={() => fileInput.current?.click()} disabled={logoBusy} className={secondaryButtonClass}>
                        <ArrowUpTrayIcon className="-ml-0.5 mr-2 h-4 w-4" />
                        {logoBusy ? 'Reading…' : form.logo ? 'Replace' : 'Upload logo'}
                      </button>
                      {form.logo && (
                        <button type="button" onClick={() => setField('logo', '')} className="px-2 text-sm font-medium text-red-600 hover:text-red-500">
                          Remove
                        </button>
                      )}
                    </div>
                    <p className="mt-1.5 text-xs text-gray-500">PNG, JPEG or WebP. It is scaled down to fit a letterhead.</p>
                    <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/webp" onChange={pickLogo} className="hidden" aria-label="Logo file" />
                  </div>
                </div>
              </div>
              {text('companyName', 'Company Name', { required: true, maxLength: 255, hint: 'As it should appear on invoices' })}
              {text('legalName', 'Legal Name', { maxLength: 255, hint: 'The registered name, when it differs' })}
              {text('companyCode', 'Short Code', { maxLength: 20, placeholder: 'e.g. ACME' })}
              <Field label="About" htmlFor="cs-description" className="sm:col-span-2">
                <textarea
                  id="cs-description"
                  rows={2}
                  maxLength={1000}
                  value={form.description}
                  onChange={(event) => setField('description', event.target.value)}
                  className={inputClass}
                  placeholder="What the company makes or does"
                />
              </Field>
            </>,
          )}

          {section(
            'address',
            <>
              <Field label="Address" htmlFor="cs-address" required error={errors.address} className="sm:col-span-2">
                <textarea
                  id="cs-address"
                  rows={2}
                  maxLength={500}
                  value={form.address}
                  onChange={(event) => setField('address', event.target.value)}
                  className={fieldClass('address')}
                  placeholder="Building, street, area"
                />
              </Field>
              {text('city', 'City', { maxLength: 100 })}
              <Field label="State" htmlFor="cs-state" error={errors.state}>
                {isIndia && stateIsListed ? (
                  <select id="cs-state" value={form.state} onChange={(event) => setField('state', event.target.value)} className={fieldClass('state')}>
                    <option value="">Select a state</option>
                    {INDIAN_STATES.map((entry) => (
                      <option key={entry.gstCode} value={entry.name}>
                        {entry.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input id="cs-state" type="text" maxLength={100} value={form.state} onChange={(event) => setField('state', event.target.value)} className={fieldClass('state')} />
                )}
              </Field>
              {text('pincode', 'Pincode', { maxLength: 6, placeholder: '6 digits' })}
              {text('country', 'Country', { maxLength: 100 })}
            </>,
          )}

          {section(
            'contact',
            <>
              {text('phone', 'Phone', { type: 'tel', maxLength: 20 })}
              {text('mobile', 'Mobile', { type: 'tel', maxLength: 20 })}
              {text('email', 'Email', { type: 'email', maxLength: 255, placeholder: 'accounts@yourcompany.com' })}
              {text('website', 'Website', { maxLength: 255, placeholder: 'https://www.yourcompany.com' })}
            </>,
          )}

          {section(
            'tax',
            <>
              <Field
                label="GST Number (GSTIN)"
                htmlFor="cs-gstNumber"
                error={errors.gstNumber}
                hint={gstState ? `${gstState.name} (${gstState.gstCode}) · PAN ${gstPan}` : '15 characters, for example 27ABCDE1234F1Z5'}
              >
                <input
                  id="cs-gstNumber"
                  type="text"
                  maxLength={15}
                  value={form.gstNumber}
                  onChange={(event) => setField('gstNumber', event.target.value)}
                  className={cn(fieldClass('gstNumber'), 'font-mono tracking-wide')}
                />
                {canFillFromGst && canEdit && (
                  <button type="button" onClick={fillFromGst} className="mt-1 text-xs font-medium text-indigo-600 hover:text-indigo-500">
                    Use this state and PAN
                  </button>
                )}
              </Field>
              {text('panNumber', 'PAN', { maxLength: 10, mono: true, hint: '10 characters; part of the GST number' })}
              {text('tanNumber', 'TAN', { maxLength: 10, mono: true, hint: 'For tax deducted at source' })}
              {text('cinNumber', 'CIN', { maxLength: 21, mono: true, hint: 'Company identification number, 21 characters' })}
            </>,
          )}

          {section(
            'bank',
            <>
              {text('bankName', 'Bank Name', { maxLength: 50 })}
              {text('accountHolderName', 'Account Holder', { maxLength: 100, hint: 'Usually the legal name of the company' })}
              {text('accountNumber', 'Account Number', { maxLength: 34, mono: true })}
              {text('ifscCode', 'IFSC Code', { maxLength: 11, mono: true, placeholder: 'e.g. HDFC0001234' })}
              {text('bankBranch', 'Branch', { maxLength: 100 })}
              {text('swiftCode', 'SWIFT Code', { maxLength: 11, mono: true, hint: 'For payments from abroad' })}
            </>,
          )}

          {section(
            'regional',
            <>
              <Field label="Currency" htmlFor="cs-currency">
                <select id="cs-currency" value={form.currency} onChange={(event) => setField('currency', event.target.value)} className={inputClass}>
                  {CURRENCIES.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Language" htmlFor="cs-language">
                <select id="cs-language" value={form.language} onChange={(event) => setField('language', event.target.value)} className={inputClass}>
                  {LANGUAGES.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Time Zone" htmlFor="cs-timezone">
                <select id="cs-timezone" value={form.timezone} onChange={(event) => setField('timezone', event.target.value)} className={inputClass}>
                  {(TIMEZONES.includes(form.timezone) ? TIMEZONES : [form.timezone, ...TIMEZONES]).map((zone) => (
                    <option key={zone} value={zone}>
                      {zone}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Financial Year Starts In" htmlFor="cs-financialYearStartMonth" hint="April for the Indian financial year">
                <select
                  id="cs-financialYearStartMonth"
                  value={form.financialYearStartMonth}
                  onChange={(event) => setField('financialYearStartMonth', event.target.value)}
                  className={inputClass}
                >
                  {MONTHS.map((month, index) => (
                    <option key={month} value={String(index + 1)}>
                      {month}
                    </option>
                  ))}
                </select>
              </Field>
            </>,
          )}

          {section(
            'invoices',
            <>
              <Field
                label="Payment Due In (days)"
                htmlFor="cs-paymentTermDays"
                required
                error={errors.paymentTermDays}
                hint="Used when the customer has no payment terms of its own"
              >
                <input
                  id="cs-paymentTermDays"
                  type="number"
                  min="0"
                  max="365"
                  step="1"
                  value={form.paymentTermDays}
                  onChange={(event) => setField('paymentTermDays', event.target.value)}
                  className={fieldClass('paymentTermDays')}
                />
              </Field>
              <Field
                label="Order Over Credit Limit"
                htmlFor="cs-creditLimitAction"
                hint={form.creditLimitAction === 'block' ? 'A quote cannot become an order while the customer is over the limit' : 'The order goes through; the customer shows on the over-limit list'}
              >
                <select
                  id="cs-creditLimitAction"
                  value={form.creditLimitAction || 'warn'}
                  onChange={(event) => setField('creditLimitAction', event.target.value)}
                  className={fieldClass('creditLimitAction')}
                >
                  <option value="warn">Allow and warn</option>
                  <option value="block">Block the order</option>
                </select>
              </Field>
              <Field
                label="Purchase Approval Limit (₹)"
                htmlFor="cs-purchaseApprovalLimit"
                error={errors.purchaseApprovalLimit}
                hint="Purchase orders above this amount need a role allowed to approve above the limit. 0 means no limit."
              >
                <input
                  id="cs-purchaseApprovalLimit"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.purchaseApprovalLimit}
                  onChange={(event) => setField('purchaseApprovalLimit', event.target.value)}
                  className={fieldClass('purchaseApprovalLimit')}
                />
              </Field>
              <div className="hidden sm:block" />
              <Field label="Terms and Conditions" htmlFor="cs-invoiceTerms" className="sm:col-span-2" hint={`${form.invoiceTerms.length} of 2000 characters`}>
                <textarea
                  id="cs-invoiceTerms"
                  rows={4}
                  maxLength={2000}
                  value={form.invoiceTerms}
                  onChange={(event) => setField('invoiceTerms', event.target.value)}
                  className={inputClass}
                  placeholder="e.g. Payment within the due date. Interest at 18% a year on overdue amounts."
                />
              </Field>
              <Field label="Notes" htmlFor="cs-invoiceNotes" className="sm:col-span-2" hint={`${form.invoiceNotes.length} of 1000 characters`}>
                <textarea
                  id="cs-invoiceNotes"
                  rows={2}
                  maxLength={1000}
                  value={form.invoiceNotes}
                  onChange={(event) => setField('invoiceNotes', event.target.value)}
                  className={inputClass}
                  placeholder="e.g. Thank you for your business."
                />
              </Field>
            </>,
          )}
        </fieldset>

        {/* Completeness and the letterhead as it will print */}
        <aside className="space-y-6 xl:sticky xl:top-4 xl:self-start">
          <div className={cn(cardClass, 'p-4')}>
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900">Profile</h2>
              <span className="text-sm font-semibold text-gray-900 tabular-nums">{isLoading ? '—' : `${percent}%`}</span>
            </div>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-100">
              <div className={cn('h-full rounded-full', percent === 100 ? 'bg-green-500' : 'bg-indigo-600')} style={{ width: `${isLoading ? 0 : percent}%` }} />
            </div>
            <ul className="mt-3 space-y-0.5">
              {SECTIONS.map((entry) => {
                const state = sectionState(entry.id);
                // Sections with nothing to check always have their defaults
                const complete = state.done === state.total;
                return (
                  <li key={entry.id}>
                    <a href={`#section-${entry.id}`} className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
                      <span className="flex items-center gap-2">
                        <CheckCircleIcon className={cn('h-4 w-4', complete ? 'text-green-600' : 'text-gray-300')} />
                        {entry.title}
                      </span>
                      {state.total > 0 && (
                        <span className="text-xs text-gray-500 tabular-nums">
                          {state.done}/{state.total}
                        </span>
                      )}
                    </a>
                  </li>
                );
              })}
            </ul>
            {percent < 100 && !isLoading && (
              <p className="mt-3 border-t border-gray-100 pt-3 text-xs text-gray-500">
                Still missing: {CHECKLIST.filter((item) => !item.done(form)).map((item) => item.label.toLowerCase()).join(', ')}.
              </p>
            )}
          </div>

          <div className={cardClass}>
            <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3">
              <BuildingOffice2Icon className="h-5 w-5 text-gray-400" />
              <h2 className="text-sm font-semibold text-gray-900">On an invoice</h2>
            </div>
            <div className="p-4" data-testid="letterhead-preview">
              <div className="rounded-md border border-gray-200 p-4">
                {form.logo && <img src={form.logo} alt="" className="mb-2 max-h-12 max-w-[10rem] object-contain" />}
                <p className={cn('text-base font-semibold', form.companyName.trim() ? 'text-gray-900' : 'text-gray-300')}>
                  {form.companyName.trim() || 'Company name'}
                </p>
                <p className={cn('mt-0.5 text-xs', previewAddress ? 'text-gray-600' : 'text-gray-300')}>{previewAddress || 'Address'}</p>
                {form.gstNumber.trim() && (
                  <p className="mt-1 text-xs text-gray-600">
                    <span className="font-semibold">GSTIN:</span> {form.gstNumber.trim()}
                  </p>
                )}
                {(form.phone.trim() || form.email.trim()) && (
                  <p className="text-xs text-gray-600">{[form.phone.trim(), form.email.trim()].filter(Boolean).join(' · ')}</p>
                )}
                {(form.bankName.trim() || form.accountNumber.trim()) && (
                  <div className="mt-3 border-t border-gray-100 pt-2 text-xs text-gray-600">
                    <p className="font-semibold text-gray-700">Bank details</p>
                    <p>{[form.bankName.trim(), form.bankBranch.trim()].filter(Boolean).join(', ')}</p>
                    {form.accountNumber.trim() && <p>A/c {form.accountNumber.trim()}</p>}
                    {form.ifscCode.trim() && <p>IFSC {form.ifscCode.trim()}</p>}
                  </div>
                )}
              </div>
              <p className="mt-3 text-xs text-gray-500">
                New invoices start with these details and are due in {form.paymentTermDays || '0'} days unless the customer has its own terms.
                Invoices already made keep what they were made with.{' '}
                <Link to="/invoices" className="font-medium text-indigo-600 hover:text-indigo-500">
                  Open invoices
                </Link>
              </p>
            </div>
          </div>
        </aside>
      </div>

      {/* Save bar */}
      {canEdit && (
        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-gray-200 bg-white/95 backdrop-blur lg:left-64">
          {/* The right padding keeps the buttons clear of the assistant button in the corner */}
          <div className="flex items-center justify-between gap-3 py-3 pl-4 pr-24 sm:pl-6 lg:pl-8">
            <p className={cn('text-sm', errorCount > 0 ? 'text-red-600' : dirty ? 'text-amber-700' : 'text-gray-500')}>
              {errorCount > 0
                ? `${errorCount} ${errorCount === 1 ? 'field needs' : 'fields need'} attention`
                : dirty
                  ? 'You have unsaved changes'
                  : isSetUp
                    ? 'Everything is saved'
                    : 'Nothing saved yet'}
            </p>
            <div className="flex gap-3">
              <button type="button" onClick={discard} disabled={!dirty || save.isPending} className={secondaryButtonClass}>
                Discard
              </button>
              <button type="submit" disabled={!dirty || save.isPending || isLoading} className={primaryButtonClass}>
                {save.isPending ? 'Saving…' : isSetUp ? 'Save Changes' : 'Set Up Company'}
              </button>
            </div>
          </div>
        </div>
      )}
    </form>
  );
};

export default CompanySettingsScreen;
